// How fresh the numbers are, in words (BACKLOG L2, PLAN-2026-09-25 N5).
//
// FRESHNESS IS SAID, NEVER IMPLIED BY A ZERO. A game that has kicked off and
// whose lines have not landed leaves 0 on the board, and a 0 reads as a
// score. So while any game in the period is waiting for its lines the screen
// says when the numbers were last updated — at every detail level, because it
// is what keeps a 0–0 from reading as a result, and the same sentence is at
// Standard and Everything (Simple removes numbers; it never adds words).
//
// ONCE EVERYTHING THAT HAS KICKED OFF HAS LANDED, the time the numbers were
// brought up to date is a detail, and it goes where details go: Standard and
// Everything. Simple shows nothing then, which removes a sentence and adds
// none.
//
// The server says the state and the instant (`stats` on the view); the words
// and the time zone are this client's. The time is the reader's zone, named,
// by the rule every time here follows (`whenInZone`, B-181).

import { Icon } from '../../kit/Icon';
import { whenInZone, type StatsFreshnessShape } from '@fsp/domain';
import { useAtLeast } from '../../data/detail';
import { useReaderZone } from '../../data/hooks';
import { useLeagueNow } from '../../data/demo';

/** The sentence for a state, or null when there is nothing to say at this level. */
export function statsSentence(
  stats: StatsFreshnessShape | null | undefined,
  zone: string | null,
  now: number,
  standard: boolean,
): string | null {
  if (!stats) return null;
  const when = stats.asOf !== null && zone !== null ? whenInZone(stats.asOf, zone, now) : null;
  if (stats.state === 'delayed') {
    // "A few hours after games end": nflverse rebuilds after each game window
    // and the worker asks again in the hours after (B-210); it said "overnight"
    // until 2026-09-30, which that check made untrue.
    // TODO(F1): a live feed's delay reads differently ("Stats delayed since 3:12").
    return when === null
      ? 'Stats land a few hours after games end'
      : `Stats land a few hours after games end · last updated ${when}`;
  }
  if (stats.state === 'final' && standard && when !== null) return `Stats updated ${when}`;
  // `none`: nothing has kicked off. `live`: the numbers are live, which the
  // live dot already says.
  return null;
}

export function StatsNote({
  stats,
  spaced = false,
}: {
  stats: StatsFreshnessShape | null | undefined;
  /** A little room above, under a row of scores (Home). */
  spaced?: boolean;
}) {
  const standard = useAtLeast('standard');
  const zone = useReaderZone();
  // The league's moment: the demo league's numbers are as of its held Tuesday.
  const now = useLeagueNow();
  const sentence = statsSentence(stats, zone, now, standard);
  if (sentence === null) return null;
  return (
    <div
      className={`fsp-sub fsp-stats-note ${spaced ? 'fsp-stats-note-spaced' : ''}`}
      data-state={stats?.state}
    >
      <Icon name="pending" size="sm" />
      <span>{sentence}</span>
    </div>
  );
}
